import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  GetProcessWheelsRequest,
  ProcessWheel,
} from "@/modules/administration/features/ProcessWheelManagement/api/getProcessWheels/types";
import { getProcessWheels } from "@/modules/administration/features/ProcessWheelManagement/api/getProcessWheels/getProcessWheelsApi";
import {
  CreateProcessWheelRequest,
  CreateProcessWheelResponse,
} from "@/modules/administration/features/ProcessWheelManagement/api/createProcessWheel/types";
import { createProcessWheel } from "@/modules/administration/features/ProcessWheelManagement/api/createProcessWheel/createProcessWheelApi";
import {
  UpdateProcessWheelRequest,
  UpdateProcessWheelResponse,
} from "@/modules/administration/features/ProcessWheelManagement/api/updateProcessWheel/types";
import { updateProcessWheel } from "@/modules/administration/features/ProcessWheelManagement/api/updateProcessWheel/updateProcessWheelApi";
import { deleteProcessWheel } from "@/modules/administration/features/ProcessWheelManagement/api/deleteProcessWheel/deleteProcessWheelApi";

interface ProcessWheelState {
  processWheels: ProcessWheel[];
  totalCount: number;
  loadingList: boolean;
  listError: string | null;

  loadingCreate: boolean;
  createError: string | null;

  loadingUpdate: boolean;
  updateError: string | null;

  loadingDelete: boolean;
  deleteError: string | null;
}

const initialState: ProcessWheelState = {
  processWheels: [],
  totalCount: 0,
  loadingList: false,
  listError: null,

  loadingCreate: false,
  createError: null,

  loadingUpdate: false,
  updateError: null,

  loadingDelete: false,
  deleteError: null,
};

const processWheelSlice = createSlice({
  name: "processWheel",
  initialState,
  reducers: {
    setLoadingList(state, action: PayloadAction<boolean>) {
      state.loadingList = action.payload;
    },
    setListError(state, action: PayloadAction<string | null>) {
      state.listError = action.payload;
    },
    setProcessWheelsData(
      state,
      action: PayloadAction<{
        processWheels: ProcessWheel[];
        totalCount: number;
      }>,
    ) {
      state.processWheels = action.payload.processWheels;
      state.totalCount = action.payload.totalCount;
    },

    setLoadingCreate(state, action: PayloadAction<boolean>) {
      state.loadingCreate = action.payload;
    },
    setCreateError(state, action: PayloadAction<string | null>) {
      state.createError = action.payload;
    },

    setLoadingUpdate(state, action: PayloadAction<boolean>) {
      state.loadingUpdate = action.payload;
    },
    setUpdateError(state, action: PayloadAction<string | null>) {
      state.updateError = action.payload;
    },

    setLoadingDelete(state, action: PayloadAction<boolean>) {
      state.loadingDelete = action.payload;
    },
    setDeleteError(state, action: PayloadAction<string | null>) {
      state.deleteError = action.payload;
    },
  },
});

export const {
  setLoadingList,
  setListError,
  setProcessWheelsData,
  setLoadingCreate,
  setCreateError,
  setLoadingUpdate,
  setUpdateError,
  setLoadingDelete,
  setDeleteError,
} = processWheelSlice.actions;

export default processWheelSlice.reducer;

export const getProcessWheelsAction =
  (
    params?: GetProcessWheelsRequest,
    onSuccess?: (processWheels: ProcessWheel[]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingList(true));
    dispatch(setListError(null));
    try {
      const response = await getProcessWheels(params);
      const processWheels = response.data;
      dispatch(
        setProcessWheelsData({
          processWheels,
          totalCount: response.meta?.totalCount ?? 0,
        }),
      );
      if (onSuccess) {
        onSuccess(processWheels);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch process wheels.";
      dispatch(setListError(message));
    } finally {
      dispatch(setLoadingList(false));
    }
  };

export const createProcessWheelAction =
  (
    requestData: CreateProcessWheelRequest,
    onSuccess?: (createdData: CreateProcessWheelResponse["data"]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingCreate(true));
    dispatch(setCreateError(null));
    try {
      const response = await createProcessWheel(requestData);
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to create process wheel.";
      dispatch(setCreateError(message));
    } finally {
      dispatch(setLoadingCreate(false));
    }
  };

export const updateProcessWheelAction =
  (
    uuid: string,
    requestData: UpdateProcessWheelRequest,
    onSuccess?: (updatedData: UpdateProcessWheelResponse["data"]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingUpdate(true));
    dispatch(setUpdateError(null));
    try {
      const response = await updateProcessWheel(uuid, requestData);
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update process wheel.";
      dispatch(setUpdateError(message));
    } finally {
      dispatch(setLoadingUpdate(false));
    }
  };

export const deleteProcessWheelAction =
  (uuid: string, onSuccess?: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingDelete(true));
    dispatch(setDeleteError(null));
    try {
      await deleteProcessWheel(uuid);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to delete process wheel.";
      dispatch(setDeleteError(message));
    } finally {
      dispatch(setLoadingDelete(false));
    }
  };
